import React, { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { family_members } from "../enums";
import useSubsectionCrud from "./useSubsectionCrud";

const purple = "#2E2868";

const FamilyHistorySubsection = ({
  subjectId,
  isCreate,
  form,
  familyHistory,
  setFamilyHistory,
  isOpen,
  onToggle,
  saveSubject,
  loadSubject,
  getConditions,
}) => {
  const { saveOne, deleteOne } = useSubsectionCrud({
    subjectId,
    saveSubject,
    loadSubject,
  });

  const [showAddFamilyHistory, setShowAddFamilyHistory] = useState(false);
  const [editingFamilyIndex, setEditingFamilyIndex] = useState(null);

  const [conditions, setConditions] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const [newFamilyHistory, setNewFamilyHistory] = useState({
    id: null,
    family_member: "",
    condition_id: null, // null => custom entry
    condition_name: "",
    notes: "",
  });

  // --- Load Conditions (no duplicate custom option) ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await getConditions();
      if (!mounted) return;
      setConditions(res || []);
    })();
    return () => {
      mounted = false;
    };
  }, [getConditions]);

  const handleFamilyHistoryChange = (field, val) =>
    setNewFamilyHistory((prev) => ({ ...prev, [field]: val }));

  const resetForm = () => {
    setNewFamilyHistory({
      id: null,
      family_member: "",
      condition_id: null,
      condition_name: "",
      notes: "",
    });
    setEditingFamilyIndex(null);
    setShowAddFamilyHistory(false);
    setIsCustomSelected(false);
  };

  // --- Helpers: safe id matching (works with numeric or string ids) ---
  const findConditionById = (rawId) => {
    if (rawId === "" || rawId == null) return null;
    if (rawId === "__CUSTOM__") return { id: null, name: "Custom Condition" };

    return (
      (conditions || []).find((c) => String(c?.id) === String(rawId)) || null
    );
  };

  const conditionOptions = useMemo(() => {
    return (conditions || []).map((c) => {
      const isCustom = c?.id == null;
      return {
        value: isCustom ? "__CUSTOM__" : String(c.id),
        label: c?.name ?? String(c?.id ?? ""),
        isCustom,
        raw: c,
      };
    });
  }, [conditions]);

  const selectedConditionValue = useMemo(() => {
    if (isCustomSelected) return "__CUSTOM__";
    return newFamilyHistory.condition_id == null
      ? ""
      : String(newFamilyHistory.condition_id);
  }, [isCustomSelected, newFamilyHistory.condition_id]);

  const syncFromFreshSubject = (fresh) => {
    if (!fresh?.subject_family_medical_history) return;
    setFamilyHistory(fresh.subject_family_medical_history);
  };

  const saveFamilyHistory = async () => {
    // Validation
    if (!newFamilyHistory.family_member) return;

    const hasCondition =
      newFamilyHistory.condition_id != null ||
      !!newFamilyHistory.condition_name?.trim();
    if (!hasCondition) return;

    //  Payload whitelist (NO age_diagnosed)
    const payloadObject = {
      ...(newFamilyHistory.id && { id: newFamilyHistory.id }),
      family_member: newFamilyHistory.family_member,
      condition_id: newFamilyHistory.condition_id ?? null,
      condition_name:
        newFamilyHistory.condition_id != null
          ? null
          : newFamilyHistory.condition_name.trim(),
      notes: (newFamilyHistory.notes || "").trim() || null,
    };

    const updatedList =
      editingFamilyIndex !== null
        ? familyHistory.map((item, idx) =>
            idx === editingFamilyIndex ? { ...item, ...payloadObject } : item,
          )
        : [...familyHistory, payloadObject];

    // Create Mode (client-side)
    if (isCreate && form?.setValue) {
      setFamilyHistory(updatedList);
      form.setValue({
        key: "subject_family_medical_history",
        value: updatedList,
      });
      resetForm();
      return;
    }

    // Edit Mode (Backend) - shared pattern
    await saveOne({
      key: "subject_family_medical_history",
      payloadObject,
      syncFromFreshSubject,
    });

    resetForm();
  };

  const deleteCurrentFamilyHistory = async () => {
    if (isCreate || editingFamilyIndex === null) return;

    const item = familyHistory[editingFamilyIndex];
    if (!item?.id) return;

    await deleteOne({
      key: "subject_family_medical_history",
      id: item.id,
      syncFromFreshSubject,
    });

    resetForm();
  };

  return (
    <div className="mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div
          className="d-flex align-items-center"
          onClick={onToggle}
          style={{ cursor: "pointer" }}
        >
          <i
            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            style={{ color: purple, fontSize: "14px", marginRight: "6px" }}
          />
          <h6 className="fw-bold m-0" style={{ color: purple }}>
            Family Medical History ({familyHistory.length})
          </h6>
        </div>

        {/* ADD Button */}
        <span
          onClick={() => {
            setShowAddFamilyHistory(true);
            setEditingFamilyIndex(null);
            setNewFamilyHistory({
              id: null,
              family_member: "",
              condition_id: null,
              condition_name: "",
              notes: "",
            });
            setIsCustomSelected(false);
          }}
          style={{
            fontSize: "13px",
            color: purple,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          + Add
        </span>
      </div>

      {/* FORM */}
      {showAddFamilyHistory && (
        <div
          className="p-3 mb-3 position-relative"
          style={{ background: "#f2f2f7", borderRadius: "12px" }}
        >
          <span
            onClick={() => setShowAddFamilyHistory(false)}
            style={{
              position: "absolute",
              right: "12px",
              top: "10px",
              cursor: "pointer",
              color: purple,
              fontWeight: "bold",
              fontSize: "23px",
            }}
          >
            ×
          </span>

          {/* Family Member */}
          <div className="mb-2">
            <label className="mb-1">Family Member</label>
            <select
              value={newFamilyHistory.family_member || ""}
              onChange={(e) =>
                handleFamilyHistoryChange("family_member", e.target.value)
              }
              className="form-control rounded-pill border-0"
            >
              <option value="">Select</option>
              {family_members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id}
                </option>
              ))}
            </select>
          </div>

          {/* Condition */}
          <div className="mb-2">
            <label className="mb-1">Condition</label>
            <select
              value={selectedConditionValue}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "__CUSTOM__") {
                  setIsCustomSelected(true);
                  setNewFamilyHistory((prev) => ({
                    ...prev,
                    condition_id: null,
                    condition_name: "",
                  }));
                  return;
                }

                const selected = findConditionById(val);
                setIsCustomSelected(false);
                setNewFamilyHistory((prev) => ({
                  ...prev,
                  condition_id: selected?.id ?? null,
                  condition_name: selected?.name ?? "",
                }));
              }}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select condition</option>
              {conditionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Condition Name */}
          {isCustomSelected && (
            <div className="mb-2">
              <label className="mb-1">Custom Condition Name</label>
              <input
                value={newFamilyHistory.condition_name}
                onChange={(e) =>
                  handleFamilyHistoryChange("condition_name", e.target.value)
                }
                className="form-control rounded-pill border-0"
                placeholder="Enter condition name"
              />
            </div>
          )}

          {/* Notes */}
          <div className="mb-3">
            <label className="mb-1">Notes</label>
            <textarea
              rows={2}
              value={newFamilyHistory.notes}
              onChange={(e) =>
                handleFamilyHistoryChange("notes", e.target.value)
              }
              className="form-control rounded-3 border-0"
            />
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-sm"
              onClick={saveFamilyHistory}
              style={{
                background: purple,
                color: "#fff",
                borderRadius: "20px",
                padding: "4px 18px",
              }}
            >
              Save
            </button>

            {!isCreate && editingFamilyIndex !== null && (
              <button
                className="btn btn-sm"
                onClick={deleteCurrentFamilyHistory}
                style={{
                  background: "#c0392b",
                  color: "#fff",
                  borderRadius: "20px",
                  padding: "4px 18px",
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* LIST */}
      {isOpen &&
        familyHistory.map((item, idx) => (
          <div
            key={item.id ?? idx}
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ fontSize: "14px", gap: "8px" }}
          >
            <div>
              <div>
                {item.family_member} | {item.condition_name}
              </div>
              <div style={{ fontSize: "12px", color: "#777" }}>
                {item.notes || "Family condition details"}
              </div>
            </div>

            {!isCreate && (
              <FontAwesomeIcon
                icon={faPen}
                style={{ cursor: "pointer", color: purple, fontSize: "14px" }}
                onClick={() => {
                  setNewFamilyHistory({
                    id: item.id ?? null,
                    family_member: item.family_member ?? "",
                    condition_id: item.condition_id ?? null,
                    condition_name: item.condition_name ?? "",
                    notes: item.notes ?? "",
                  });

                  setEditingFamilyIndex(idx);
                  setShowAddFamilyHistory(true);

                  // custom if no condition_id but has name
                  setIsCustomSelected(
                    item.condition_id == null && !!item.condition_name,
                  );
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default FamilyHistorySubsection;
